import React from "react";

import { GenericExternalLink } from "@components/generic-external-link";
import { useUser } from "@components/user-context";
import { NorthbeamUtmPlatform } from "@features/utm-guide";

import { CheckUrlValidity } from "../check-url-validity";

import { AdRoll } from "./ad-roll";
import { AvantLink } from "./avant-link";
import { CNN5Things } from "./cnn-5-things";
import { Criteo } from "./criteo";
import { Facebook } from "./facebook";
import { Google } from "./google";
import { Grin } from "./grin";
import { ImpactRadius } from "./impact-radius";
import { Klaviyo } from "./klaviyo";
import { LiveIntent } from "./live-intent";
import { Other } from "./other";
import { Outbrain } from "./outbrain";
import { Partnerize } from "./partnerize";
import { Pinterest } from "./pinterest";
import { Postscript } from "./postscript";
import { Rakuten } from "./rakuten";
import { RetentionScience } from "./retention-science";
import { Revcontent } from "./revcontent";
import { ShareASale } from "./share-a-sale";
import { Snapchat } from "./snapchat";
import { StackAdapt } from "./stack-adapt";
import { Taboola } from "./taboola";
import { TikTok } from "./tiktok";
import { Twitter } from "./twitter";
import { YahooDSP } from "./yahoo-dsp";

type PlatformComponentInfo = {
  Component: React.FC<Record<never, never>>;
  knowledgeBaseLink?: string;
};

const DEFAULT_KNOWLEDGE_BASE_LINK =
  "https://docs.northbeam.io/docs/tracking-for-other-platforms";

const UtmGuidePlatformComponents: {
  [key in NorthbeamUtmPlatform]: PlatformComponentInfo;
} = {
  [NorthbeamUtmPlatform.AVANT_LINK]: { Component: AvantLink },
  [NorthbeamUtmPlatform.CNN5_THINGS]: { Component: CNN5Things },
  [NorthbeamUtmPlatform.KLAVIYO]: {
    Component: Klaviyo,
    knowledgeBaseLink:
      "https://docs.northbeam.io/docs/setting-up-klaviyo-for-northbeam#part-1-setting-account-wide-utms",
  },
  [NorthbeamUtmPlatform.META]: {
    Component: Facebook,
    knowledgeBaseLink:
      "https://docs.northbeam.io/docs/setting-up-facebook-ads-tracking#step-2-insert-northbeam-utm-parameters",
  },
  [NorthbeamUtmPlatform.GOOGLE]: {
    Component: Google,
    knowledgeBaseLink:
      "https://docs.northbeam.io/docs/setting-up-google-ads-for-northbeam#step-2-insert-northbeam-utm-parameters",
  },
  [NorthbeamUtmPlatform.SNAPCHAT]: {
    Component: Snapchat,
    knowledgeBaseLink:
      "https://docs.northbeam.io/docs/tracking-for-snapchat-ads#step-2-insert-northbeam-utm-parameters",
  },
  [NorthbeamUtmPlatform.RAKUTEN]: {
    Component: Rakuten,
    knowledgeBaseLink: "https://docs.northbeam.io/docs/tracking-for-rakuten",
  },
  [NorthbeamUtmPlatform.PINTEREST]: {
    Component: Pinterest,
    knowledgeBaseLink:
      "https://docs.northbeam.io/docs/setting-up-pinterest-ads-for-northbeam#step-2-insert-northbeam-utm-parameters",
  },
  [NorthbeamUtmPlatform.TABOOLA]: { Component: Taboola },
  [NorthbeamUtmPlatform.OUTBRAIN]: { Component: Outbrain },
  [NorthbeamUtmPlatform.STACK_ADAPT]: { Component: StackAdapt },
  [NorthbeamUtmPlatform.SHARE_A_SALE]: { Component: ShareASale },
  [NorthbeamUtmPlatform.PARTNERIZE]: { Component: Partnerize },
  [NorthbeamUtmPlatform.REVCONTENT]: { Component: Revcontent },
  [NorthbeamUtmPlatform.IMPACT_RADIUS]: { Component: ImpactRadius },
  [NorthbeamUtmPlatform.TIKTOK]: {
    Component: TikTok,
    knowledgeBaseLink:
      "https://docs.northbeam.io/docs/setting-up-tiktok-ads-for-northbeam#step-2-insert-northbeam-utm-parameters",
  },
  [NorthbeamUtmPlatform.CRITEO]: { Component: Criteo },
  [NorthbeamUtmPlatform.TWITTER]: {
    Component: Twitter,
    knowledgeBaseLink:
      "https://docs.northbeam.io/docs/tracking-for-twitter-ads",
  },
  [NorthbeamUtmPlatform.AD_ROLL]: { Component: AdRoll },
  [NorthbeamUtmPlatform.GRIN]: { Component: Grin },
  [NorthbeamUtmPlatform.YAHOO_DSP]: { Component: YahooDSP },
  [NorthbeamUtmPlatform.RETENTION_SCIENCE]: { Component: RetentionScience },
  [NorthbeamUtmPlatform.LIVE_INTENT]: { Component: LiveIntent },
  [NorthbeamUtmPlatform.POST_SCRIPT]: { Component: Postscript },
  [NorthbeamUtmPlatform.MICROSOFT]: {
    Component: () => (
      <p className="mb-0">
        Sit back and relax! You don&apos;t need to do anything for this
        platform, we&apos;ve already got you covered.
      </p>
    ),
    knowledgeBaseLink:
      "https://docs.northbeam.io/docs/tracking-for-microsoft#1-do-i-need-specific-utm-parameters",
  },
  [NorthbeamUtmPlatform.OTHER]: { Component: Other },
};

export const PlatformComponent = ({
  platform,
}: {
  readonly platform: NorthbeamUtmPlatform;
}): JSX.Element => {
  const { user } = useUser();

  const { Component, knowledgeBaseLink = DEFAULT_KNOWLEDGE_BASE_LINK } =
    UtmGuidePlatformComponents[platform];

  return (
    <div className="flex flex-col gap-3">
      {knowledgeBaseLink && (
        <p className="pb-0">
          <em>
            Learn more about this integration{" "}
            <GenericExternalLink href={knowledgeBaseLink}>
              here.
            </GenericExternalLink>
          </em>
        </p>
      )}
      <Component />
      {user.isAdmin && <CheckUrlValidity platform={platform} />}
    </div>
  );
};
